import { EventSourceInput } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import FullCalendar from "@fullcalendar/react";
import { Css, Palette } from "@homebound/beam";
import { createRef, useEffect, useMemo } from "react";
import { TaskDetailsPage_PlanTaskFragment } from "src/fragments/circularFragments.generated";
import { DynamicSchedulesTaskSnapshot_TaskSnapshotFragment } from "src/routes/projects/dynamic-schedules/components/useScheduleSnapshots.generated";
import { isWithinDateRange } from "src/utils/temporal";
import { Temporal } from "temporal-polyfill";
import { TaskColorLegend } from "../../schedule-v2/components/TaskColorLegend";
import { DateIcons } from "../calendar/dynamicSchedulesCalendarUtils";
import { getTemporalBusinessDaysOptions } from "../utils";
import "./CalendarCard.css";

type CalendarCardProps = {
  task: TaskDetailsPage_PlanTaskFragment;
  taskSnapshot: DynamicSchedulesTaskSnapshot_TaskSnapshotFragment | undefined;
};

export function CalendarCard(props: CalendarCardProps) {
  const { task, taskSnapshot } = props;
  const { workDays, customWorkableDays, schedule } = task;
  const calendarRef = createRef<FullCalendar>();

  // Iterate over the startDate+durationInDays and create an event for each day
  const taskWorkInterval = useMemo(() => {
    const businessDaysOptions = getTemporalBusinessDaysOptions(customWorkableDays, schedule.schedulingExclusionDates);

    const taskWorkInterval: Temporal.PlainDate[] = [];
    if (task.startDate && task.durationInDays) {
      for (let i = 0; i < task.durationInDays; i++) {
        const date = task.startDate.addBusinessDays(i, businessDaysOptions);
        taskWorkInterval.push(date);
      }
    }
    return taskWorkInterval;
  }, [task.startDate, task.durationInDays, customWorkableDays, schedule.schedulingExclusionDates]);

  const events: EventSourceInput = useMemo(
    () =>
      [
        // Estimated work days, iterate over the work days and create an event for each
        ...taskWorkInterval
          .map((date) => ({
            id: date.toString(),
            date: date.toString(),
            allDay: true,
            title: date.day.toString(),
            backgroundColor: Palette.Gray300,
          }))
          .filter((event) => !workDays.some((workDay) => workDay.workDay.toString() === event.id)),
        // Actual work days
        ...workDays.map((workDay) => ({
          id: workDay.id,
          date: workDay.workDay.toString(),
          allDay: true,
          // The title will be the day of the month
          title: workDay.workDay.day.toString(),
          backgroundColor: Palette.Blue200,
        })),
        // Add the original start date last so it will be removed in the `uniqueBy` call if it overlaps with the current range
        taskSnapshot?.startDate && {
          id: "originalStartDate",
          date: taskSnapshot.startDate.toString(),
          allDay: true,
          title: taskSnapshot.startDate.day.toString(),
          backgroundColor: Palette.Yellow200,
        },
      ]
        .compact()
        .uniqueBy((event) => String(event.date)),
    [taskWorkInterval, workDays, taskSnapshot],
  );

  const delayFlags = useMemo(() => task.scheduleFlags, [task.scheduleFlags]);

  const isFlagWithinRange = (createdAtDate: Temporal.ZonedDateTime, date: Date) => {
    return dateToPlainDate(date).equals(createdAtDate.toPlainDate());
  };

  // Check if the task is planned for the current month
  const isInCurrentMonth = useMemo(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      const calendarDate = dateToPlainDate(calendarApi.getDate());
      return isWithinDateRange(calendarDate, task.startDate, task.endDate);
    }
    return false;
  }, [calendarRef, task.endDate, task.startDate]);

  // If the task is not in the current month we will render the calendar in the month of the task
  useEffect(() => {
    if (calendarRef.current && !isInCurrentMonth) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(task.startDate.toString());
    }
  }, [calendarRef, isInCurrentMonth, task.startDate]);

  return (
    <div css={Css.gc("1 / span 2").gr(1).df.fdc.bgWhite.br8.hPx(600).$}>
      <div className="hb-dynamic-schedules-calendar-card" css={baseStyles}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin]}
          height={"calc(100% - 30px)"}
          customButtons={{
            // This is a hack to have a header that is not a button
            header: { text: "Task Status", click: () => {} },
            empty: { text: "", click: () => {} },
          }}
          events={events}
          editable={false}
          headerToolbar={{ start: "header", center: "prev title next", end: "empty" }}
          dayHeaderContent={({ text }) => {
            return <div css={Css.lg.gray500.mb1.$}>{text[0]}</div>;
          }}
          dayCellContent={({ dayNumberText, isOther, date }) => {
            const currentDayDelayFlags = delayFlags?.filter((f) => isFlagWithinRange(f.createdAt, date));
            return (
              <div css={Css.relative.df.aic.jcc.w100.h100.$}>
                {currentDayDelayFlags.nonEmpty && (
                  <DateIcons
                    delayFlag={currentDayDelayFlags?.map((d) => d.reason?.title).join(", ")}
                    // TODO: Add the logic for materialDrop and tradePartnerAssigned
                  />
                )}
                <div css={Css.lg.if(isOther).dn.else.gray800.$}>{dayNumberText}</div>
              </div>
            );
          }}
          eventContent={({ event }) => {
            const currentDayDelayFlags = delayFlags?.filter((f) => isFlagWithinRange(f.createdAt, event.start!));
            return (
              <div css={Css.relative.df.aic.jcc.w100.h100.$}>
                {currentDayDelayFlags.nonEmpty && (
                  <DateIcons
                    delayFlag={currentDayDelayFlags?.map((d) => d.reason?.title).join(", ")}
                    // TODO: Add the logic for materialDrop and tradePartnerAssigned
                  />
                )}
                <div css={Css.lg.gray800.$}>{event.title}</div>
              </div>
            );
          }}
        />
        <TaskColorLegend
          title={""}
          items={[
            { color: Palette.Blue200, label: "A day that this task was worked on" },
            { color: Palette.Gray300, label: "Estimated work days / No work done" },
            { color: Palette.Yellow200, label: "Original Start Date" },
          ]}
        />
      </div>
    </div>
  );
}

const baseStyles = Css.br8.h100.$;

function dateToPlainDate(date: Date) {
  return Temporal.PlainDate.from({ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() });
}
